import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import {
  decideHardwareRequest,
  retryHardwareRequestZendesk,
  undoHardwareRequestDecision,
} from '@/api/hardwareRequests';
import { ApiError } from '@/api/client';
import { useAuth } from '@/auth/useAuth';
import { formatDateTime } from '@/lib/dateFormat';
import { canRecordDecision, decisionReading, toneClass } from '@/lib/hardwareRequest';
import { TicketLinkControl } from './TicketLinkControl';
import type {
  HardwareRequestApprovalStatus,
  HardwareRequestDetail,
} from '@/types/oscar';

type Decision = Exclude<HardwareRequestApprovalStatus, 'Pending'>;

const VERB: Record<Decision, string> = {
  Approved: 'Approve',
  Declined: 'Decline',
  Referred: 'Refer back',
};

/**
 * The decision panel — Phase 2 of `OnsiteCore-hardware-lifecycle-design.md`.
 *
 * The IT Asset Manager approves, declines, or refers a request back for more
 * information.
 *
 * ⛔ THREE THINGS THIS DOES NOT DO, and all three are said on screen rather than
 * left to be discovered:
 *
 * 1. It allocates nothing. Allocation is a separate act on the same page
 *    (FulfilmentPanel, Phase 4), and it is separate because approving and
 *    choosing which laptop are different decisions on different days.
 * 2. It raises no purchase — Phase 5.
 * 3. ⚠ THIS NOTE USED TO SAY the decision never reached Zendesk. It does, since
 *    Phase 2b went live 30/09/2026 — and the panel says so on screen. The
 *    write-back outcome is rendered by ZendeskState below, in three states,
 *    because "Zendesk returned 200" is not evidence that it landed.
 */
/**
 * Whether the decision reached the ticket.
 *
 * ⛔ THREE STATES, NOT TWO, and merging any pair of them is the bug this whole
 * phase exists to end:
 *
 *   confirmed  — a READ-BACK saw the comment. Not "Zendesk returned 200", which
 *                is not evidence: it answers 200 and drops fields it dislikes.
 *   failed     — tried, and the ticket is STALE. Somebody has to act.
 *   never      — not attempted, because there is no ticket id. Most historic
 *                requests went out through the email fallback, which leaves
 *                none. That is not a failure and must not read as one.
 */
function ZendeskState({
  request,
  onRetry,
  retrying,
}: {
  request: HardwareRequestDetail;
  onRetry: () => void;
  retrying: boolean;
}) {
  if (request.ZendeskCommentAtUtc) {
    return (
      <span>
        Sent to Zendesk{request.EffectiveTicketId ? ` #${request.EffectiveTicketId}` : ''} on{' '}
        {formatDateTime(request.ZendeskCommentAtUtc)}
        {request.ZendeskStatusSet ? `, ticket set to ${request.ZendeskStatusSet}` : ''}. Confirmed
        by reading the ticket back, not by the API's reply.
      </span>
    );
  }

  if (request.ZendeskCommentError) {
    return (
      <span>
        <strong>This decision did not reach Zendesk.</strong>{' '}
        {request.EffectiveTicketId
          ? `Ticket #${request.EffectiveTicketId} is out of date.`
          : 'The ticket is out of date.'}{' '}
        {request.ZendeskCommentError}
        {request.ZendeskWriteAttempts > 1 && (
          <> Tried {request.ZendeskWriteAttempts} times — this is not looking transient.</>
        )}{' '}
        <button
          type="button"
          onClick={onRetry}
          disabled={retrying}
          className="font-semibold text-navy underline disabled:opacity-50"
        >
          {retrying ? 'Sending…' : 'Try again'}
        </button>
      </span>
    );
  }

  return (
    <span>
      <strong>No Zendesk ticket to update.</strong> This request was raised through the email
      fallback, which leaves no ticket id — so there is nothing to annotate. Not a failure.
    </span>
  );
}

export function DecisionPanel({ request }: { request: HardwareRequestDetail }) {
  const { canApproveHardware } = useAuth();
  const queryClient = useQueryClient();
  const [choice, setChoice] = useState<Decision | null>(null);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reading = decisionReading(request.ApprovalStatus);
  const open = canRecordDecision(request.ApprovalStatus);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['hardware-request', request.SubmissionId] });
    // The list carries the decision too, and a stale queue is how somebody
    // decides the same request twice.
    void queryClient.invalidateQueries({ queryKey: ['hardware-requests'] });
  };

  const decide = useMutation({
    mutationFn: () => decideHardwareRequest(request.SubmissionId, choice!, notes.trim() || undefined),
    onSuccess: () => {
      setChoice(null);
      setNotes('');
      setError(null);
      refresh();
    },
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.message : 'Could not record the decision.'),
  });

  const retryZendesk = useMutation({
    mutationFn: () => retryHardwareRequestZendesk(request.SubmissionId),
    onSuccess: () => { setError(null); refresh(); },
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.message : 'Could not send the decision to Zendesk.'),
  });

  const undo = useMutation({
    mutationFn: () => undoHardwareRequestDecision(request.SubmissionId),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.message : 'Could not undo the decision.'),
  });

  // ⛔ Mirrors the API and CK_HwReqApproval_NotesWhenRefused: a decline or a
  // referral needs a reason, an approval does not. Disabling the button is the
  // third layer of that rule, not the only one.
  const needsReason = choice !== null && choice !== 'Approved';
  const canSubmit = choice !== null && (!needsReason || notes.trim().length > 0);

  return (
    <section className="mb-6 rounded border border-border bg-white p-4 shadow-sm">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading text-lg font-bold text-navy">Decision</h2>
        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${toneClass[reading.tone]}`}>
          {reading.label}
        </span>
      </header>

      {!open && (
        <div className="mt-3 border-t border-border pt-3">
          <p className="text-sm text-darkGrey">
            {reading.label} by <strong>{request.ApprovedBy ?? 'unknown'}</strong>
            {request.ApprovedAtUtc ? ` on ${formatDateTime(request.ApprovedAtUtc)}` : ''}.
          </p>
          {request.ApprovalNotes && (
            <p className="mt-2 whitespace-pre-wrap rounded bg-bg px-3 py-2 text-sm text-darkGrey">
              {request.ApprovalNotes}
            </p>
          )}
          {/* The asset manager or an admin (02/11/2026: it was admin-only, so the
              asset manager had no way to revoke). The API enforces the same rule
              and audits every undo. */}
          {canApproveHardware && (
            <button
              type="button"
              onClick={() => undo.mutate()}
              disabled={undo.isPending}
              className="mt-3 text-xs font-semibold text-navy hover:underline disabled:opacity-50"
            >
              {undo.isPending ? 'Undoing…' : 'Undo this decision'}
            </button>
          )}
        </div>
      )}

      {/*
        Helpdesk can see this page because they fulfil the work. Saying why the
        buttons are absent beats showing buttons that 403 — and beats showing
        nothing, which reads as a missing feature.
      */}
      {open && !canApproveHardware && (
        <p className="mt-3 border-t border-border pt-3 text-sm text-darkGrey/70">
          Awaiting a decision from the IT Asset Manager. You can see this request because you
          fulfil the work; deciding it needs the <code>oscar-asset-manager</code> role.
        </p>
      )}

      {open && canApproveHardware && (
        <div className="mt-3 border-t border-border pt-3">
          <div className="flex flex-wrap gap-2">
            {(Object.keys(VERB) as Decision[]).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => {
                  setChoice(s);
                  setError(null);
                }}
                className={`rounded border px-3 py-1.5 text-sm font-semibold ${
                  choice === s
                    ? 'border-navy bg-navy text-white'
                    : 'border-border bg-white text-navy hover:bg-bg'
                }`}
              >
                {VERB[s]}
              </button>
            ))}
          </div>

          {choice !== null && (
            <div className="mt-3">
              <label className="flex flex-col text-xs font-semibold text-darkGrey">
                {needsReason ? 'Reason (required)' : 'Note (optional)'}
                <textarea
                  aria-label={needsReason ? 'Reason' : 'Note'}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal"
                  placeholder={
                    needsReason
                      ? 'What the requester needs to know — this is the only thing they get back.'
                      : ''
                  }
                />
              </label>
              {needsReason && notes.trim().length === 0 && (
                <p className="mt-1 text-xs text-darkGrey/70">
                  A {choice === 'Declined' ? 'decline' : 'referral'} without a reason leaves the
                  requester with nothing to act on.
                </p>
              )}
              <button
                type="button"
                onClick={() => decide.mutate()}
                disabled={!canSubmit || decide.isPending}
                className="mt-3 rounded bg-orange px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
              >
                {decide.isPending ? 'Recording…' : `Record ${VERB[choice].toLowerCase()}`}
              </button>
            </div>
          )}

        </div>
      )}

      {/*
        ⛔ THE CAVEAT HAS TO OUTLIVE THE DECISION.

        It used to render only while the request was undecided, which put it on
        screen when it was least needed and removed it at the exact moment
        somebody would go looking for the ticket. Anthony declined request #17 on
        30/09 and reasonably expected Zendesk to have been updated; by then the
        panel had stopped saying otherwise.

        So it now renders in BOTH states, and once a decision exists it names the
        ticket — because "the ticket still needs updating" is advice you can act
        on, and "decisions do not reach Zendesk" is only trivia.
      */}
      {(!open || canApproveHardware) && (
        <p className="mt-3 flex items-start gap-2 rounded border border-border bg-bg px-3 py-2 text-xs text-darkGrey/80">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          {open ? (
            <span>
              Recording a decision does not allocate anything — approve first, then allocate
              below. The decision is sent to the Zendesk ticket automatically; the outcome
              appears here once it is made.
            </span>
          ) : (
            <ZendeskState request={request} onRetry={() => retryZendesk.mutate()} retrying={retryZendesk.isPending} />
          )}
        </p>
      )}

      {/* ⚠ A SIBLING OF THE NOTE, NOT INSIDE IT. The control renders a <div>,
          and the block above is a <p> — nesting one in the other is invalid
          HTML that React only grumbles about at runtime.

          ⛔ Shown whatever the write-back state. The obvious moment to link a
          ticket is after a failure, but somebody who already knows the ticket
          was closed by mistake should not have to decide first and fail once to
          be offered the way out. */}
      {!open && <TicketLinkControl request={request} />}

      {error && <p className="mt-3 text-sm text-red">{error}</p>}
    </section>
  );
}
